/**
 * useGameEngine — runs the dispatch-911 client-side game loop.
 *
 * Encapsulates the 5 setInterval timers that drive the game:
 *   1. Snapshot persist (5 s)         — flushes money + calls + dispatches to backend
 *   2. Call spawner (SPAWN_INTERVAL)  — spawns new emergencies near a random station
 *   3. Hard expiry tick (30 s)        — fails any pending call older than 6 h
 *   4. On-scene resolve tick (500 ms) — closes calls once all required units are on scene
 *   5. Dispatch animation tick (100 ms) — moves trucks, marks arrivals, counts helpers
 *
 * State is owned by Game.jsx; this hook reads the latest values from `stateRef`
 * (which Game updates every render) and writes back via the passed setters.
 * Returns `spawnCall` so Game.jsx can fire it from the dev hook.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  EMERGENCY_TYPES,
  EXTRA_CONCURRENT_SLOTS,
  HARD_EXPIRY_SECONDS,
  PATIENT_ASSESS_SECONDS,
  SPAWN_INTERVAL_MS,
  SPAWN_RADIUS_KM,
  distanceKm,
  countOwnedMedics,
  generatePatientsForCall,
  pickEmergencyType,
  rollCallUpgrade,
} from "@/lib/gameConfig";
import { US_CITIES, randomAddress, randomCityNear } from "@/lib/cities";
import { getRoute, pointAtProgress, reverseRoute } from "@/lib/routing";
import { reverseGeocode } from "@/lib/geocode";
import { playCallAlert } from "@/lib/sound";
import { helperUpdate as apiHelperUpdate, snapshotState, admitPatient, callResolved, admitArrestee } from "@/lib/api";
import { nextCallId, nextDispatchId } from "@/hooks/gameIds";

// Aggregate on-scene counts by type for a given call
// Shared helpers — also imported by Game.jsx to enforce "required units must
// remain on scene" rule when the user tries to clear a unit mid-mission.
export function onSceneByType(dispatches, callId) {
  const m = {};
  dispatches.forEach((d) => {
    if (d.callId === callId && d.onScene && !d.returning) {
      const t = d.apparatusType || "Engine";
      m[t] = (m[t] || 0) + 1;
    }
  });
  return m;
}

export function meetsRequirements(required, onScene, alternates) {
  // OR-group semantics: satisfied if EITHER `required` OR any option in
  // `alternates` is fully met. Both are unit-type → count maps.
  //
  // "Supervisor" is a virtual role: Sheriff OR Police Chief on scene
  // satisfies each requested Supervisor slot. We expand onScene with a
  // synthetic `Supervisor` bucket here so mission authors can just write
  // `{"Supervisor": 1}` without worrying about which rank showed up.
  const augmented = {
    ...onScene,
    Supervisor:
      (onScene["Supervisor"] || 0) +
      (onScene["Sheriff"] || 0) +
      (onScene["Police Chief"] || 0),
  };
  const options = [required || {}].concat(Array.isArray(alternates) ? alternates : []);
  return options.some((opt) =>
    Object.entries(opt).every(([type, n]) => (augmented[type] || 0) >= n)
  );
}

export default function useGameEngine({
  loaded,
  stateRef,
  setPlayer,
  setCalls,
  setDispatches,
  setApparatusState,
  setStations,
  pushRadio,
  onCallResolved,
  onCallUpgraded,
  spawnIntervalMs,
}) {
  // ---------- 1-second ticker forces re-renders for timer countdowns ----------
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ---------- 1. Snapshot every 5 s (heartbeat) ----------
  useEffect(() => {
    if (!loaded) return;
    const id = setInterval(() => {
      const p = stateRef.current.player;
      snapshotState({
        money: p.money,
        active_calls: stateRef.current.calls.map((c) => ({ ...c })),
        active_dispatches: stateRef.current.dispatches.map((d) => ({ ...d })),
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(id);
  }, [loaded, stateRef]);

  // ---------- 1b. Event-driven snapshot whenever dispatches change shape ----------
  // Catches transport state transitions (transport-start, unload-flip, return)
  // that the 5s heartbeat would miss if the user closes the tab right after
  // clicking — historically caused medics to freeze mid-route on reload.
  // Debounced 300 ms so rapid tick-mutations don't spam the backend.
  const dispatchSnapshotTimer = useRef(null);
  const lastShapeRef = useRef("");
  useEffect(() => {
    if (!loaded) return;
    // Build a stable "shape" string of the dispatches that ignores transient
    // coordinate updates (lat/lng) but captures lifecycle fields. Without
    // this, every 100ms movement tick would queue a write.
    const shape = stateRef.current.dispatches
      .map((d) =>
        [
          d.id,
          d.callId,
          !!d.onScene,
          !!d.returning,
          !!d.transporting,
          !!d.unloading,
          !!d.queued,
          d.transportHospitalId || "",
          d.transportReservationId || "",
          d.transportPatientId || "",
          d.startedAt,
          d.durationSec,
        ].join("/")
      )
      .sort()
      .join("|");
    if (shape === lastShapeRef.current) return;
    lastShapeRef.current = shape;
    if (dispatchSnapshotTimer.current) clearTimeout(dispatchSnapshotTimer.current);
    dispatchSnapshotTimer.current = setTimeout(() => {
      const p = stateRef.current.player;
      snapshotState({
        money: p.money,
        active_calls: stateRef.current.calls.map((c) => ({ ...c })),
        active_dispatches: stateRef.current.dispatches.map((d) => ({ ...d })),
      }).catch(() => {});
    }, 300);
    return () => {
      if (dispatchSnapshotTimer.current) clearTimeout(dispatchSnapshotTimer.current);
    };
    // Intentional: depend on dispatches; stateRef.current.dispatches is
    // refreshed by Game.jsx so React sees the new identity each render.
  }, [loaded, stateRef, stateRef.current.dispatches]);

  // ---------- 1c. Beacon snapshot on tab-close / page-hide ----------
  // sendBeacon is fire-and-forget AND survives the page unload — regular
  // fetch() calls get cancelled by the browser when the tab closes. This
  // guarantees the LATEST dispatch state lands in MongoDB right before the
  // game session disconnects, so the engine can resume cleanly on next login.
  useEffect(() => {
    if (!loaded) return;
    const flushBeacon = () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/game/snapshot`;
        const token = localStorage.getItem("dispatch911_token");
        if (!token) return;
        const p = stateRef.current.player;
        const body = JSON.stringify({
          money: p.money,
          active_calls: stateRef.current.calls.map((c) => ({ ...c })),
          active_dispatches: stateRef.current.dispatches.map((d) => ({ ...d })),
        });
        // sendBeacon doesn't accept custom headers (no Authorization). We
        // fall back to a tiny synchronous fetch with `keepalive: true` —
        // this is the modern, headers-supporting equivalent.
        if (navigator.sendBeacon && false) {
          // Disabled — backend requires Bearer auth which sendBeacon can't set.
          const blob = new Blob([body], { type: "application/json" });
          navigator.sendBeacon(url, blob);
        } else {
          fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body,
            keepalive: true, // critical — lets fetch outlive the page
          }).catch(() => {});
        }
      } catch (_e) { /* best-effort */ }
    };
    // Use both `pagehide` (modern, fires on actual close/nav) and
    // `visibilitychange` (fires when the tab is hidden) — together they
    // cover desktop close, mobile background, and BFCache restores.
    const onPageHide = () => flushBeacon();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushBeacon();
    };
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [loaded, stateRef]);

  // ---------- spawnCall — used by the spawner tick AND the dev hook ----------
  const spawnCall = useCallback(
    (ss) => {
      const station = ss[Math.floor(Math.random() * ss.length)];
      const center = randomCityNear(station.lat, station.lng, SPAWN_RADIUS_KM);
      const nearestCity = US_CITIES
        .map((c) => ({ c, d: distanceKm(c, center) }))
        .sort((a, b) => a.d - b.d)[0]?.c;
      const cityName = nearestCity?.name || "Unknown City, US";
      const typeKey = pickEmergencyType(ss);
      if (!typeKey) return;
      const t = EMERGENCY_TYPES[typeKey];
      const callId = nextCallId();
      // Roll for an upgrade once at spawn time. If it lands, store the target
      // id; the actual `upgradeAtMs` schedule is set when the FIRST unit
      // arrives on scene (i.e. the call becomes `revealed`), so upgrades can
      // only happen while units are present.
      const upgradeTargetId = rollCallUpgrade(t);
      const newCall = {
        id: callId,
        type: typeKey,
        lat: center.lat,
        lng: center.lng,
        address: randomAddress(cityName),
        addressLoading: true,
        createdAt: Date.now(),
        requiredUnits: { ...t.required_units },
        alternates: Array.isArray(t.alternates) ? t.alternates.map((a) => ({ ...a })) : undefined,
        onSceneDurationSec: t.on_scene_duration,
        state: "pending",
        onSceneStartedAt: null,
        revealed: false,
        upgradeTargetId: upgradeTargetId || undefined,
        upgradeAtMs: undefined, // armed on reveal
        // Service classification — tags the call so the UI + engine can
        // apply police-specific logic (arrestee spawn, jail-cell transport)
        // without adding a bunch of ad-hoc type-key string checks.
        serviceType: t.service_type || "fire",
      };
      // Police calls with an arrest_chance_pct roll for a single arrestee
      // that units on scene need to handle. Kept simple: one arrestee per
      // call max; status flow is pending → detained (auto after ~30 s of
      // Patrol/Deputy on scene) → transported (Phase 2: jail cell hold).
      if ((t.service_type === "police") && Number(t.arrest_chance_pct) > 0) {
        if (Math.random() * 100 < Number(t.arrest_chance_pct)) {
          newCall.arrestees = [
            { id: `arr_${callId}`, status: "pending" },
          ];
        }
      }
      setCalls((cs) => [...cs, newCall]);
      playCallAlert();

      // Resolve a real street address asynchronously and patch the call when it returns.
      reverseGeocode(center.lat, center.lng).then((addr) => {
        if (!addr) {
          setCalls((cs) =>
            cs.map((c) => (c.id === callId ? { ...c, addressLoading: false } : c))
          );
          return;
        }
        setCalls((cs) =>
          cs.map((c) =>
            c.id === callId ? { ...c, address: addr, addressLoading: false } : c
          )
        );
      });
    },
    [setCalls]
  );

  // ---------- 2. Call spawner ----------
  useEffect(() => {
    if (!loaded) return;
    const intervalMs = spawnIntervalMs && spawnIntervalMs >= 1000 ? spawnIntervalMs : SPAWN_INTERVAL_MS;
    const id = setInterval(() => {
      const ss = stateRef.current.stations;
      if (ss.length === 0) return;
      const maxConcurrent = ss.length + EXTRA_CONCURRENT_SLOTS;
      if (stateRef.current.calls.length >= maxConcurrent) return;
      spawnCall(ss);
    }, intervalMs);
    return () => clearInterval(id);
  }, [loaded, spawnCall, stateRef, spawnIntervalMs]);

  // ---------- 3. Hard expiry tick (6 h wall clock for pending calls) ----------
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      const cutoff = HARD_EXPIRY_SECONDS * 1000;
      setCalls((prev) => {
        const failed = [];
        const remaining = [];
        prev.forEach((c) => {
          if (c.state === "pending" && now - c.createdAt >= cutoff) failed.push(c);
          else remaining.push(c);
        });
        if (failed.length === 0) return prev;
        const failedIds = new Set(failed.map((f) => f.id));
        setDispatches((ds) =>
          ds.map((d) => {
            if (d.callId && failedIds.has(d.callId) && !d.returning) {
              const home = d.stationId
                ? stateRef.current.stations.find((s) => s.id === d.stationId)
                : stateRef.current.hospitals.find((h) => h.id === d.hospitalId);
              if (!home) return d;
              const coords = d.onScene
                ? reverseRoute({ coords: d.routeCoords }).coords
                : [[d.lat, d.lng], [home.lat, home.lng]];
              return {
                ...d,
                returning: true,
                onScene: false,
                callId: null,
                routeCoords: coords,
                startedAt: Date.now(),
                durationSec: d.onScene
                  ? (d.outboundDurationSec || d.durationSec)
                  : Math.max(5, d.durationSec * 0.5),
              };
            }
            return d;
          })
        );
        setPlayer((p) => ({ ...p }));
        failed.forEach((c) =>
          pushRadio(
            "expired",
            `${EMERGENCY_TYPES[c.type]?.label || c.type} at ${c.address || "unknown address"} — call expired (6h, no response)`
          )
        );
        return remaining;
      });
    }, 30000); // 6 h cutoff doesn't need fine resolution
    return () => clearInterval(id);
  }, [setCalls, setDispatches, setPlayer, stateRef, pushRadio]);

  // ---------- 4. On-scene resolve tick ----------
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      const stations = stateRef.current.stations;
      // Detect resolved calls OUTSIDE the setCalls updater so the side effect
      // (pushRadio) fires exactly once even under StrictMode's double-render.
      const cur = stateRef.current.calls;
      // A call with patients cannot resolve until every patient has either
      //   - refused transport ("refused"), OR
      //   - been delivered to a hospital ("delivered"), OR
      //   - is already being transported ("in_transit" / "queued").
      // i.e. no patient is still un-assessed AND no patient is sitting on
      // scene awaiting Transport ("none" + needsTransport).
      const patientsCleared = (c) => {
        if (!Array.isArray(c.patients) || c.patients.length === 0) return true;
        return c.patients.every((p) => {
          if (!p.assessed) return false;
          if (!p.needsTransport) return true;
          return p.transportStatus === "in_transit"
              || p.transportStatus === "queued"
              || p.transportStatus === "delivered"
              || p.transportStatus === "refused";
        });
      };
      const wouldResolve = cur.filter(
        (c) =>
          c.state === "on_scene" &&
          c.onSceneStartedAt &&
          now - c.onSceneStartedAt >= c.onSceneDurationSec * 1000 &&
          // Gem-paid Quick Finish bypasses the patient gate — player paid
          // for an instant close, so patients on scene aren't a blocker.
          (c.quickFinish || patientsCleared(c))
      );
      if (wouldResolve.length === 0) return;
      // Force any pending upgrade to fire FIRST. The upgrade itself is applied
      // by the dispatch-animation tick (5a) when it sees `upgradeAtMs <= now`,
      // so we just pull the schedule forward here and skip resolution this
      // tick. The call will resolve normally after its new (upgraded) timer.
      const forcedUpgrades = [];
      const resolved = [];
      wouldResolve.forEach((c) => {
        if (c.upgradeTargetId) {
          forcedUpgrades.push(c.id);
        } else {
          resolved.push(c);
        }
      });
      if (forcedUpgrades.length) {
        const ids = new Set(forcedUpgrades);
        setCalls((prev) =>
          prev.map((c) => (ids.has(c.id) ? { ...c, upgradeAtMs: now - 1 } : c))
        );
      }
      if (resolved.length === 0) return;
      const resolvedIds = new Set(resolved.map((r) => r.id));
      let moneyGain = 0;
      resolved.forEach((c) => {
        const t = EMERGENCY_TYPES[c.type];
        moneyGain += t.reward;
        pushRadio(
          "resolved",
          `${t.label} cleared at ${c.address || "unknown address"} — incident closed (+$${t.reward.toLocaleString()})`
        );
      });
      // Notify the parent so retention/achievement counters can be bumped.
      // Pass full call objects so the caller can compute severity-based XP.
      onCallResolved?.(resolved);
      setCalls((prev) => prev.filter((c) => !resolvedIds.has(c.id)));
      // Apply call reward through the server-side donation distribution so
      // the alliance bank gets its cut. The endpoint deducts the donation
      // and returns the NET — that's what we add to the player's local money.
      if (moneyGain > 0) {
        (async () => {
          try {
            const r = await callResolved(moneyGain);
            setPlayer((p) => ({ ...p, money: p.money + (r.net ?? moneyGain) }));
            if (r.donation && r.donation > 0) {
              pushRadio("alliance", `Donated $${r.donation.toLocaleString()} to alliance bank`);
            }
          } catch (_e) {
            // If server call fails (e.g. offline), credit the full amount so
            // the player isn't penalized — the alliance bank will lose this
            // donation. Acceptable trade-off for offline play.
            setPlayer((p) => ({ ...p, money: p.money + moneyGain }));
          }
        })();
      }
      setDispatches((ds) => {
        const freedIds = ds
          .filter((d) => d.callId && resolvedIds.has(d.callId) && d.onScene)
          .map((d) => d.apparatusId);
        if (freedIds.length) {
          setApparatusState((as) => {
            const nx = { ...as };
            freedIds.forEach((id) => {
              if (nx[id]) nx[id] = { ...nx[id], available: true };
            });
            return nx;
          });
        }
        const next = [];
        ds.forEach((d) => {
          if (d.callId && resolvedIds.has(d.callId) && d.onScene) {
            const home = d.stationId
              ? stations.find((s) => s.id === d.stationId)
              : (stateRef.current.hospitals || []).find((h) => h.id === d.hospitalId);
            if (!home) {
              next.push(d);
              return;
            }
            // The outbound route only ends at home when it ALSO started
            // there (e.g. station → call). If this unit was DIVERTED
            // mid-return to the current call, the outbound was actually
            // `mid-road-point → call`; reversing it would send the unit
            // back to that random mid-road point, not home — the user
            // experiences this as the truck "wandering away from the
            // station". A separate async refetch (kicked off below this
            // updater so React strict-mode can't double-fire it) will
            // replace the straight-line placeholder with a real road route
            // once OSRM responds.
            const start = d.routeCoords?.[0];
            const startedFromHome =
              Array.isArray(start) &&
              Math.abs(start[0] - home.lat) < 0.0005 &&
              Math.abs(start[1] - home.lng) < 0.0005;
            const newId = nextDispatchId();
            let routeCoords;
            const durationSec = d.outboundDurationSec || d.durationSec || 60;
            if (startedFromHome && Array.isArray(d.routeCoords) && d.routeCoords.length >= 2) {
              routeCoords = [...d.routeCoords].reverse();
            } else {
              routeCoords = [[d.lat, d.lng], [home.lat, home.lng]];
            }
            next.push({
              ...d,
              id: newId,
              callId: null,
              onScene: false,
              returning: true,
              routeCoords,
              startedAt: now,
              durationSec,
            });
          } else {
            next.push(d);
          }
        });
        return next;
      });

      // Async OSRM refetch for any diverted-mid-return cases. Runs OUTSIDE
      // the setDispatches updater so React strict-mode's double-invocation
      // can't trigger duplicate network requests. Each fetch patches the
      // dispatch in place once the real route arrives; the unit keeps
      // animating along the straight-line placeholder in the meantime.
      const dispatchesNow = stateRef.current.dispatches;
      const refetchReturns = [];
      dispatchesNow.forEach((d) => {
        if (!d.callId || !resolvedIds.has(d.callId) || !d.onScene) return;
        const home = d.stationId
          ? stations.find((s) => s.id === d.stationId)
          : (stateRef.current.hospitals || []).find((h) => h.id === d.hospitalId);
        if (!home) return;
        const start = d.routeCoords?.[0];
        const startedFromHome =
          Array.isArray(start) &&
          Math.abs(start[0] - home.lat) < 0.0005 &&
          Math.abs(start[1] - home.lng) < 0.0005;
        if (startedFromHome && Array.isArray(d.routeCoords) && d.routeCoords.length >= 2) {
          return; // reverse is safe — no refetch needed
        }
        refetchReturns.push({
          apparatusId: d.apparatusId,
          origin: { lat: d.lat, lng: d.lng },
          home: { lat: home.lat, lng: home.lng },
          apparatusType: d.apparatusType,
        });
      });
      refetchReturns.forEach(({ apparatusId, origin, home, apparatusType }) => {
        (async () => {
          try {
            const r = await getRoute(origin, home, { apparatusType });
            setDispatches((dx) =>
              dx.map((x) =>
                // Match by apparatusId + returning state — the newId we
                // assigned above isn't visible out here, so we identify
                // the dispatch by the unique (apparatusId, returning=true)
                // combo. Only patch if still on the placeholder route.
                x.apparatusId === apparatusId &&
                x.returning &&
                Array.isArray(x.routeCoords) &&
                x.routeCoords.length === 2
                  ? {
                      ...x,
                      routeCoords: r.coords,
                      durationSec: r.durationSec,
                      startedAt: Date.now(),
                    }
                  : x
              )
            );
          } catch (_e) {
            /* Straight-line placeholder remains; unit still reaches home. */
          }
        })();
      });
    }, 500);
    return () => clearInterval(id);
  }, [pushRadio, setApparatusState, setCalls, setDispatches, setPlayer, stateRef, onCallResolved]);

  // ---------- 5. Dispatch animation tick (the "mega-tick") ----------
  useEffect(() => {
    const TICK = 100;
    // Time the Medic crew spends inside the hospital after arrival before
    // starting the return leg (handing patient over to the receiving bed).
    const UNLOAD_DURATION_MS = 10_000;
    const id = setInterval(() => {
      const now = Date.now();

      // ----- 5a. Scheduled call upgrades -----
      // Upgrade fires at the random `upgradeAtMs` set at first-arrival time.
      // Allowed while the call is `pending` (more units still en route) OR
      // `on_scene` (work timer running) — anytime units are present and the
      // call hasn't been resolved/expired yet.
      const upgrades = [];
      stateRef.current.calls.forEach((c) => {
        if (!c.upgradeTargetId || !c.upgradeAtMs) return;
        if (c.state !== "pending" && c.state !== "on_scene") return;
        if (now < c.upgradeAtMs) return;
        const target = EMERGENCY_TYPES[c.upgradeTargetId];
        if (!target) return;
        upgrades.push({ id: c.id, fromType: c.type, target, targetId: c.upgradeTargetId, address: c.address, state: c.state });
      });
      if (upgrades.length) {
        const byId = new Map(
          upgrades.map((u) => [
            u.id,
            {
              type: u.targetId,
              requiredUnits: { ...u.target.required_units },
              alternates: Array.isArray(u.target.alternates)
                ? u.target.alternates.map((a) => ({ ...a }))
                : undefined,
              onSceneDurationSec: u.target.on_scene_duration,
              upgradedFrom: u.fromType,
              upgradeTargetId: undefined,
              upgradeAtMs: undefined,
              // If the call was already on_scene with the old requirements,
              // re-evaluate — the new requirements may not be met yet, so we
              // drop it back to `pending` and clear the work-timer start.
              ...(u.state === "on_scene"
                ? { state: "pending", onSceneStartedAt: null }
                : {}),
            },
          ])
        );
        setCalls((prev) =>
          prev.map((c) => (byId.has(c.id) ? { ...c, ...byId.get(c.id) } : c))
        );
        upgrades.forEach((u) => {
          pushRadio(
            "expired",
            `${EMERGENCY_TYPES[u.fromType].label} at ${u.address || "scene"} — UPGRADED to ${u.target.label}`
          );
          playCallAlert();
        });
        // Best-effort: push the upgraded snapshot to the alliance share, if any
        if (onCallUpgraded) {
          upgrades.forEach((u) => onCallUpgraded(u.id));
        }
      }

      // Compute arrivals / returns from the latest committed state in ref —
      // no side effects inside setState updaters (React may run them twice).
      const cur = stateRef.current.dispatches;
      const arrived = [];
      const returned = [];
      const delivered = []; // transporting dispatches that just reached hospital
      const unloadingDone = []; // dispatches that just finished the 10s unload
      const tickPatches = []; // { id, lat, lng, becameOnScene }
      cur.forEach((d) => {
        if (d.onScene) return;
        if (d.queued) return; // dispatch is in the 3s stagger queue — no movement yet
        // Unloading at the hospital — stationary 10-second window between
        // arrival and return-leg start. Skip movement, just check the timer.
        if (d.unloading) {
          if (d.unloadStartedAt && now - d.unloadStartedAt >= UNLOAD_DURATION_MS) {
            unloadingDone.push(d);
          }
          return;
        }
        const elapsed = (now - d.startedAt) / 1000;
        const progress = Math.min(1, elapsed / d.durationSec);
        const [lat, lng] = pointAtProgress(d.routeCoords, progress);
        if (progress >= 1) {
          if (d.returning) {
            returned.push(d);
          } else if (d.transporting) {
            // Transport leg done — admit on server + enter UNLOAD wait.
            delivered.push({ ...d, lat, lng });
          } else {
            arrived.push({
              id: d.id,
              apparatusName: d.apparatusName,
              callId: d.callId,
              helper: !!d.helper,
              sharedId: d.sharedId,
            });
            tickPatches.push({ id: d.id, lat, lng, becameOnScene: true });
          }
        } else {
          tickPatches.push({ id: d.id, lat, lng, becameOnScene: false });
        }
      });

      // Apply patches + remove returned dispatches in a single state update.
      if (tickPatches.length || returned.length) {
        const returnedIds = new Set(returned.map((r) => r.id));
        const patchById = new Map(tickPatches.map((p) => [p.id, p]));
        setDispatches((prev) =>
          prev
            .filter((d) => !returnedIds.has(d.id))
            .map((d) => {
              const p = patchById.get(d.id);
              if (!p) return d;
              return p.becameOnScene
                ? { ...d, lat: p.lat, lng: p.lng, onScene: true }
                : { ...d, lat: p.lat, lng: p.lng };
            })
        );
        if (returned.length) {
          setApparatusState((as) => {
            const nx = { ...as };
            returned.forEach((d) => {
              if (nx[d.apparatusId]) nx[d.apparatusId] = { ...nx[d.apparatusId], available: true };
            });
            return nx;
          });
        }
      }

      // Transport arrivals — admit patient on server + enter the 10-second
      // UNLOAD wait (crew walking patient inside / handing off to bed).
      // Medic stays parked at the hospital and remains UNAVAILABLE during
      // this window. The next tick flips it to RETURN once the timer ends.
      if (delivered.length) {
        delivered.forEach((d) => {
          // Jail-transport arrivals: admit arrestee, update station's
          // occupied_jail_cells server-side, then unload back to base.
          if (d.transportPoliceStationId) {
            (async () => {
              try {
                const updated = await admitArrestee(d.transportPoliceStationId, {
                  arrestee_id: d.transportArresteeId,
                  apparatus_id: d.apparatusId,
                });
                // Sync back into local station state (occupied_jail_cells etc.).
                setStations(updated.stations);
                setPlayer((p) => ({ ...p, money: updated.money }));
                pushRadio(
                  "resolved",
                  `${d.apparatusName} dropped suspect at ${d.transportPoliceStationName} — booked (+$500).`,
                );
                // Mark the arrestee in-custody on the originating call
                // so the CallDialog pill flips DETAINED → IN CUSTODY.
                setCalls((prev) => prev.map((c) => {
                  if (!Array.isArray(c.arrestees)) return c;
                  if (!c.arrestees.some((a) => a.id === d.transportArresteeId)) return c;
                  return {
                    ...c,
                    arrestees: c.arrestees.map((a) =>
                      a.id === d.transportArresteeId
                        ? { ...a, status: "transported", stationId: d.transportPoliceStationId }
                        : a
                    ),
                  };
                }));
              } catch (_e) { /* server-side admit failed — leave state as-is */ }
            })();
            // Unload wait mirrors hospital flow.
            setDispatches((ds) => ds.map((x) => x.id === d.id ? {
              ...x,
              transporting: false,
              unloading: true,
              unloadStartedAt: Date.now(),
              onScene: false,
              callId: null,
              lat: d.lat,
              lng: d.lng,
            } : x));
            return; // don't fall through to patient admit branch
          }
          (async () => {
            try {
              const a = await admitPatient(
                d.transportHospitalId,
                {
                  reservation_id: d.transportReservationId,
                  patient_id: d.transportPatientId,
                  specialty: d.transportSpecialty || null,
                },
                d.transportHospitalOwnerId || undefined,
                d.transportHospitalAllianceId || undefined,
              );
              setPlayer((p) => ({ ...p, money: (p.money || 0) + (a.reward || 0) }));
              pushRadio(
                "resolved",
                `${d.apparatusName} dropped patient at ${d.transportHospitalName} — +$${(a.reward || 0).toLocaleString()}${a.specialty_match ? " (specialty)" : ""}`,
              );
              // Mark patient delivered in the originating call (if call still exists).
              setCalls((prev) => prev.map((c) => {
                if (!Array.isArray(c.patients)) return c;
                if (!c.patients.some((p) => p.id === d.transportPatientId)) return c;
                return {
                  ...c,
                  patients: c.patients.map((p) =>
                    p.id === d.transportPatientId
                      ? { ...p, transportStatus: "delivered", deliveredHospital: d.transportHospitalName }
                      : p
                  ),
                };
              }));
            } catch (_e) { /* server-side admit failed — silently leave bed reserved */ }
          })();
          // Enter UNLOAD wait. Medic stays parked at hospital coords.
          setDispatches((ds) => ds.map((x) => x.id === d.id ? {
            ...x,
            transporting: false,
            unloading: true,
            unloadStartedAt: Date.now(),
            onScene: false,
            callId: null,
            lat: d.lat,
            lng: d.lng,
          } : x));
        });
      }

      // Unload timer finished — flip to RETURN leg & mark Medic AVAILABLE.
      if (unloadingDone.length) {
        unloadingDone.forEach((d) => {
          // Compute a fresh HOSPITAL → HOME route. Reusing `reverseRoute` on
          // the outbound coords would route us back through the CALL location
          // (since outbound was call→hospital), so we must fetch a new route.
          // While the route is in-flight we leave the dispatch parked at the
          // hospital and only flip to "returning" once the new route arrives.
          (async () => {
            let backCoords;
            let backDur;
            try {
              const r = await getRoute(
                { lat: d.lat, lng: d.lng },
                { lat: d.transportHomeLat, lng: d.transportHomeLng },
                { apparatusType: d.apparatusType },
              );
              backCoords = r.coords;
              backDur = r.durationSec;
            } catch (_e) {
              // Fall back to a straight-line route if OSRM is unavailable.
              backCoords = [[d.lat, d.lng], [d.transportHomeLat, d.transportHomeLng]];
              backDur = d.outboundDurationSec || d.durationSec || 60;
            }
            setDispatches((ds) => ds.map((x) => x.id === d.id ? {
              ...x,
              unloading: false,
              unloadStartedAt: null,
              returning: true,
              routeCoords: backCoords,
              startedAt: Date.now(),
              durationSec: backDur,
              lat: d.lat,
              lng: d.lng,
            } : x));
          })();
          // Medic is AVAILABLE during return — player can dispatch it again
          // while it's heading back to base. (Mark available immediately —
          // even if the route fetch is pending, the medic is no longer at the
          // hospital ramp.)
          setApparatusState((as) => ({
            ...as,
            [d.apparatusId]: { ...as[d.apparatusId], available: true },
          }));
        });
      }

      // Fire arrival radio entries (once each).
      // Also: when the FIRST unit of any kind arrives at a call that has
      // a `patients` config but no patients array yet, roll patients now.
      if (arrived.length > 0) {
        const firstArrivalCallIds = new Set();
        arrived.forEach((m) => {
          const c = stateRef.current.calls.find((x) => x.id === m.callId);
          if (!c) return;
          const t = EMERGENCY_TYPES[c.type];
          if (!t?.patients) return;
          if (Array.isArray(c.patients)) return; // already rolled
          // Also make sure no prior dispatch is already on scene for this call.
          const priorOnScene = stateRef.current.dispatches.some(
            (d) => d.callId === c.id && d.onScene && d.id !== m.id
          );
          if (!priorOnScene) firstArrivalCallIds.add(c.id);
        });
        if (firstArrivalCallIds.size) {
          const medicCount = countOwnedMedics(
            stateRef.current.stations,
            stateRef.current.hospitals,
          );
          setCalls((prev) =>
            prev.map((c) => {
              if (!firstArrivalCallIds.has(c.id)) return c;
              if (Array.isArray(c.patients)) return c;
              return { ...c, patients: generatePatientsForCall(EMERGENCY_TYPES[c.type], c.id, medicCount) };
            })
          );
        }
      }
      arrived.forEach((m) => {
        if (m.helper && m.sharedId) {
          // Helper unit arrived at a remote (alliance) call — notify server so the
          // requester's client can count us on scene. Best-effort, no toast spam.
          const aid = stateRef.current.allianceId;
          if (aid) {
            apiHelperUpdate(aid, m.sharedId, {
              dispatchId: m.id,
              on_scene: true,
            }).catch(() => {});
          }
          pushRadio("onscene", `${m.apparatusName} arrived at mutual-aid scene`);
          return;
        }
        const call = stateRef.current.calls.find((c) => c.id === m.callId);
        const where = call?.address || "the scene";
        pushRadio("onscene", `${m.apparatusName} arrived at ${where}`);
      });

      // Now compute pending call transitions from the freshly intended on-scene set.
      const arrivedIds = new Set(arrived.map((a) => a.id));
      const effectiveDispatches = stateRef.current.dispatches.map((d) =>
        arrivedIds.has(d.id) ? { ...d, onScene: true } : d
      );

      // Augment with on-scene helper units from MY shared calls so they count
      // toward the requester's "all units on scene" check. Each on-scene helper
      // is represented as a virtual dispatch tied to the call by its server `call.id`.
      const myShares = (stateRef.current.shares || []).filter(
        (s) => s.requester_id === stateRef.current.userId
      );
      myShares.forEach((s) => {
        const callId = s.call?.id;
        if (callId == null) return;
        (s.helpers || []).forEach((h) => {
          if (h.on_scene) {
            effectiveDispatches.push({
              id: `helper_${s.id}_${h.dispatch_id}`,
              callId,
              apparatusType: h.apparatusType || "Engine",
              apparatusName: h.apparatusName,
              onScene: true,
              returning: false,
              helper: true,
            });
          }
        });
      });
      const callPatches = []; // { id, revealed?, state?, onSceneStartedAt?, upgradeAtMs? }
      stateRef.current.calls.forEach((c) => {
        if (c.state !== "pending") return;
        const anyOnScene = effectiveDispatches.some(
          (d) => d.callId === c.id && d.onScene && !d.returning
        );
        let revealed = c.revealed;
        // Arm the upgrade schedule at FIRST arrival. The window runs from now
        // until the call would resolve (roughly = onSceneDurationSec from now).
        // The actual upgrade fires at a uniformly random moment in that window.
        let armUpgradeAt;
        if (anyOnScene && !c.revealed) {
          revealed = true;
          pushRadio(
            "assessed",
            `${EMERGENCY_TYPES[c.type]?.label || c.type} at ${c.address || "scene"} — requirements assessed`
          );
          if (c.upgradeTargetId && !c.upgradeAtMs) {
            armUpgradeAt = Date.now() + Math.random() * c.onSceneDurationSec * 1000;
          }
        }
        const onScene = onSceneByType(effectiveDispatches, c.id);
        if (meetsRequirements(c.requiredUnits, onScene, c.alternates)) {
          pushRadio(
            "allunits",
            `${EMERGENCY_TYPES[c.type]?.label || c.type} — all required units on scene, working`
          );
          callPatches.push({
            id: c.id,
            revealed: true,
            state: "on_scene",
            onSceneStartedAt: Date.now(),
            ...(armUpgradeAt ? { upgradeAtMs: armUpgradeAt } : {}),
          });
        } else if (revealed !== c.revealed) {
          callPatches.push({
            id: c.id,
            revealed,
            ...(armUpgradeAt ? { upgradeAtMs: armUpgradeAt } : {}),
          });
        }
      });

      // ----- 5d. Pause/resume WORKING-SCENE timer for on_scene calls -----
      // If a call is in `on_scene` state but the player has recalled units
      // and the on-scene tally no longer meets the requirements, we push
      // `onSceneStartedAt` forward by exactly one tick. This effectively
      // pauses the work-timer countdown (and the UI progress bar that's
      // derived from `now - onSceneStartedAt`) until enough units arrive
      // again. As soon as requirements are re-satisfied, we stop pushing
      // forward and the timer naturally resumes from where it left off.
      const pausePatches = [];
      stateRef.current.calls.forEach((c) => {
        if (c.state !== "on_scene" || !c.onSceneStartedAt) return;
        if (c.quickFinish) return; // gem-bypassed: never pause it
        // EMS-style calls: if every patient is already settled (in_transit /
        // delivered / refused), there's nothing left for a Medic to do on
        // scene. Don't pause the work timer even if the Medic has left to
        // transport — that would strand the call forever.
        const allPatientsSettled =
          Array.isArray(c.patients) &&
          c.patients.length > 0 &&
          c.patients.every(
            (p) =>
              p.assessed &&
              (!p.needsTransport ||
                p.transportStatus === "in_transit" ||
                p.transportStatus === "queued" ||
                p.transportStatus === "delivered" ||
                p.transportStatus === "refused")
          );
        if (allPatientsSettled) return;
        const onScene = onSceneByType(effectiveDispatches, c.id);
        if (!meetsRequirements(c.requiredUnits, onScene, c.alternates)) {
          // Cap forward shift so the bar never APPEARS to advance past 100%
          // while paused (e.g. if the player paused for hours).
          const elapsed = now - c.onSceneStartedAt;
          const cap = c.onSceneDurationSec * 1000;
          if (elapsed < cap) {
            pausePatches.push({ id: c.id, onSceneStartedAt: c.onSceneStartedAt + TICK });
          }
        }
      });
      if (pausePatches.length) {
        const byPauseId = new Map(pausePatches.map((p) => [p.id, p]));
        setCalls((prev) =>
          prev.map((c) => (byPauseId.has(c.id) ? { ...c, ...byPauseId.get(c.id) } : c))
        );
      }

      // ----- 5e. Patient assessment tick -----
      // For each call with patients, advance the assess progress for any
      // un-assessed patient if there's at least one Medic on scene. With 1
      // Medic on scene, full assessment takes PATIENT_ASSESS_SECONDS (30 s).
      const assessPatches = [];
      let refusalReward = 0;          // $250 per assessed patient that refuses transport
      const refusalRadio = [];        // radio log entries to push after the tick
      stateRef.current.calls.forEach((c) => {
        if (!Array.isArray(c.patients) || c.patients.length === 0) return;
        const medicCount = effectiveDispatches.filter(
          (d) => {
            if (d.callId !== c.id || !d.onScene) return false;
            const t = stateRef.current.apparatusState[d.apparatusId]?.type || d.apparatusType;
            return t === "Medic" || t === "HEMS";  // both can assess + transport
          }
        ).length;
        if (medicCount === 0) return;
        // Per-tick progress increment — fixed at 1 medic = PATIENT_ASSESS_SECONDS
        // regardless of count (user's choice 8a). Per-call override via the
        // emergency type's `assess_seconds` field (e.g. Cardiac Arrest = 120s).
        const assessSecs = EMERGENCY_TYPES[c.type]?.assess_seconds || PATIENT_ASSESS_SECONDS;
        const inc = TICK / (assessSecs * 1000);
        let changed = false;
        const next = c.patients.map((p) => {
          if (p.assessed) return p;
          const np = Math.min(1, (p.assessProgress || 0) + inc);
          if (np !== p.assessProgress) changed = true;
          const justAssessed = np >= 1;
          // Patient just finished assessment AND doesn't need transport →
          // pay the Medic a $250 "refusal" reward. Patients that DO need
          // transport will earn their reward when they're admitted at the
          // hospital instead.
          if (justAssessed && !p.needsTransport && p.transportStatus !== "refused") {
            refusalReward += 250;
            refusalRadio.push(`${EMERGENCY_TYPES[c.type]?.label || "EMS"} at ${c.address || "scene"} — patient refused transport (+$250)`);
            return {
              ...p,
              assessProgress: np,
              assessed: true,
              transportStatus: "refused",
            };
          }
          return { ...p, assessProgress: np, assessed: justAssessed };
        });
        if (changed) assessPatches.push({ id: c.id, patients: next });
      });
      if (assessPatches.length) {
        const byAId = new Map(assessPatches.map((p) => [p.id, p]));
        setCalls((prev) =>
          prev.map((c) => (byAId.has(c.id) ? { ...c, patients: byAId.get(c.id).patients } : c))
        );
      }
      // Credit refusal rewards via the server-side donation pipeline so the
      // alliance bank gets its cut (same plumbing as on-scene call rewards).
      if (refusalReward > 0) {
        const amount = refusalReward;
        refusalRadio.forEach((m) => pushRadio("resolved", m));
        (async () => {
          try {
            const r = await callResolved(amount);
            setPlayer((p) => ({ ...p, money: p.money + (r.net ?? amount) }));
            if (r.donation && r.donation > 0) {
              pushRadio("alliance", `Donated $${r.donation.toLocaleString()} to alliance bank`);
            }
          } catch (_e) {
            setPlayer((p) => ({ ...p, money: p.money + amount }));
          }
        })();
      }

      if (callPatches.length) {
        const byId = new Map(callPatches.map((p) => [p.id, p]));
        setCalls((prev) =>
          prev.map((c) => (byId.has(c.id) ? { ...c, ...byId.get(c.id) } : c))
        );
      }

      // ----- 5f. Orphan-dispatch sweep -----
      // Recover any dispatch whose `callId` no longer maps to a live call
      // (call cleared / auto-resolved before this engine marked the dispatch
      // returning — happens with mid-call upgrades where the player can't
      // meet the new requirements). Without this sweep, the unit would stay
      // marked busy in `apparatusState` forever, blocking re-dispatch.
      // Also heals EXISTING ghost saves: on next login, the freshly-loaded
      // dispatches list contains the ghost (callId=42) but active_calls no
      // longer has id=42 → the very next tick releases the unit, and the
      // snapshot heartbeat persists the fix back to MongoDB.
      // Gate on `loaded` so we never trigger during the brief load window
      // (where dispatches may have arrived but calls hasn't yet).
      if (!stateRef.current.loaded) return;
      const liveCallIds = new Set(stateRef.current.calls.map((c) => c.id));
      const orphanIds = [];
      const orphanReturning = [];
      stateRef.current.dispatches.forEach((d) => {
        if (d.returning) return;          // already heading home
        if (d.transporting || d.unloading) return; // hospital flow handles itself
        if (d.helper && d.sharedId) return; // helper rows handled by useMutualAid
        if (d.callId == null) return;     // already detached (transport leg / recall)
        if (liveCallIds.has(d.callId)) return; // call is live, leave alone
        orphanIds.push(d.id);
        // Build a return route so the truck animates home rather than
        // teleporting. Reverse current route if we have one; otherwise
        // straight-line back to its station/hospital.
        const stat = d.stationId
          ? stateRef.current.stations.find((s) => s.id === d.stationId)
          : null;
        const home = stat || (d.hospitalId
          ? (stateRef.current.hospitals || []).find((h) => h.id === d.hospitalId)
          : null);
        if (!home) return;
        const reversed = Array.isArray(d.routeCoords) && d.routeCoords.length > 1
          ? reverseRoute({ coords: d.routeCoords }).coords
          : [[d.lat, d.lng], [home.lat, home.lng]];
        orphanReturning.push({
          id: d.id,
          apparatusId: d.apparatusId,
          patch: {
            returning: true,
            onScene: false,
            callId: null,
            routeCoords: reversed,
            startedAt: Date.now(),
            durationSec: Math.max(5, (d.outboundDurationSec || d.durationSec || 30)),
          },
        });
      });
      if (orphanReturning.length) {
        const byOrphanId = new Map(orphanReturning.map((o) => [o.id, o.patch]));
        setDispatches((ds) =>
          ds.map((x) => byOrphanId.has(x.id) ? { ...x, ...byOrphanId.get(x.id) } : x)
        );
        setApparatusState((as) => {
          const next = { ...as };
          orphanReturning.forEach((o) => {
            // Mark the apparatus immediately AVAILABLE so the player can
            // re-dispatch it without waiting for the return leg to finish.
            // (Engine treats `returning && available` units as preempt-able.)
            if (next[o.apparatusId]) {
              next[o.apparatusId] = { ...next[o.apparatusId], available: true };
            }
          });
          return next;
        });
        orphanReturning.forEach((o) => {
          const d = stateRef.current.dispatches.find((x) => x.id === o.id);
          if (d) pushRadio("recall", `${d.apparatusName} cleared from closed call — returning to quarters`);
        });
      }
    }, TICK);
    return () => clearInterval(id);
  }, [pushRadio, setApparatusState, setCalls, setDispatches, setPlayer, stateRef, onCallUpgraded]);

  // ---------- Arrestee auto-detention ----------
  // Simple police-side analogue of patient assessment: once a Patrol Car
  // or Deputy is on scene of a police call with pending arrestees, count
  // 30 seconds of presence and flip the arrestee to "detained". This
  // gives the dispatcher a visible progress indicator without blocking
  // call resolution. Full jail-cell transport is Phase 2.
  useEffect(() => {
    const id = setInterval(() => {
      const calls = stateRef.current.calls || [];
      const dispatches = stateRef.current.dispatches || [];
      const now = Date.now();
      const DETAIN_MS = 30_000;
      let touched = false;
      const updates = new Map();
      calls.forEach((c) => {
        if (!Array.isArray(c.arrestees) || c.arrestees.length === 0) return;
        if (c.arrestees.every((a) => a.status !== "pending")) return;
        // Find the earliest on-scene arrival of a Patrol Car / Deputy.
        const arrivedAt = dispatches
          .filter((d) => d.callId === c.id && d.onScene && (d.apparatusType === "Patrol Car" || d.apparatusType === "Deputy"))
          .reduce((min, d) => Math.min(min, d.startedAt + (d.durationSec || 0) * 1000), Infinity);
        if (!isFinite(arrivedAt)) return;
        if (now - arrivedAt < DETAIN_MS) return;
        const nextArrestees = c.arrestees.map((a) =>
          a.status === "pending" ? { ...a, status: "detained", detainedAt: now } : a
        );
        updates.set(c.id, nextArrestees);
        touched = true;
      });
      if (touched) {
        setCalls((cs) =>
          cs.map((c) => (updates.has(c.id) ? { ...c, arrestees: updates.get(c.id) } : c))
        );
        updates.forEach((_, cid) => {
          const c = calls.find((x) => x.id === cid);
          if (c) pushRadio("dispatch", `${c.address || "Scene"} — suspect detained.`);
        });
      }
    }, 1000);
    return () => clearInterval(id);
  }, [setCalls, stateRef, pushRadio]);

  return { spawnCall };
}
